import { Avatar, Box, Button, Drawer, Grid, LinearProgress, Paper, Stack, Typography } from "@mui/material";
import { RestaurantMenu, SubdirectoryArrowRight, CheckCircle } from "@mui/icons-material";
import { useMemo, useState } from "react";
import { useSnackbar } from "../../../core/contexts/SnackbarContext";
import { useMoeda } from "../../../core/hooks/useMoeda";
import { IProdutoPedido } from "../../../pedidos/interface/IProdutoPedido";
import { IComplemento } from "../../interfaces/IComplemento";
import { IITemComplemento } from "../../interfaces/IITemComplemento";
import { IItemProduto } from "../../../pedidos/interface/IItemProduto";
import { IComplementoItensPedido } from "../../../pedidos/interface/IComplementoItensPedido";
import { IProduto } from "../../interfaces/IProduto";
import { TipoProduto } from "../../enum/TipoProduto";
import { TipoItemProduto } from "../../enum/TipoItemProduto";
import { IItemCombo } from "../../interfaces/IItemCombo";
import { calcularPrecoComplemento, tratarItensComplemento } from "../../../pedidos/utils/calcularPrecoComplemento";
import { ComplementoPaperInput } from "../DrawerPreparadoOperacao/container/ComplementoPaperInput";
import { BodyDetalhesContainer, DetalhesContainer, HeaderDetalhesContainer } from "../CardPreparadoOperacao/styles";
import { v4 } from "uuid";

interface DrawerComboOperacaoProps {
    open: boolean;
    toggleDrawer: () => void;
    produto: IProduto;
    adicionarProdutosCombo: (produtos: IProdutoPedido[]) => void;
}

function filtrarComplementosSlot(itemProduto: IProduto, allComplementos: IComplemento[]): IComplemento[] {
    if (itemProduto.tipo === TipoProduto.pizza) {
        return allComplementos.filter(c => c.descricao.endsWith(` — ${itemProduto.descricao}`));
    }
    if (itemProduto.tipo === TipoProduto.preparado) {
        return allComplementos.filter(c => c.descricao.startsWith(`${itemProduto.descricao} — `));
    }
    return [];
}

const ResumoItemCombo = ({
    descricao,
    quantidade,
    imagem,
    isIndustrializado,
    subItens,
    statusLabel,
    total,
    real
}: {
    descricao: string;
    quantidade: number;
    imagem?: string;
    isIndustrializado: boolean;
    subItens: IItemProduto[];
    statusLabel: string;
    total: number;
    real: (valor?: string | number) => string;
}) => (
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box
            sx={{
                width: 56,
                height: 56,
                flexShrink: 0,
                borderRadius: 1.5,
                overflow: "hidden",
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "action.hover",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
            }}
        >
            {imagem ? (
                <Box
                    component="img"
                    src={imagem}
                    alt={descricao}
                    sx={{
                        width: isIndustrializado ? "70%" : "100%",
                        height: isIndustrializado ? "70%" : "100%",
                        objectFit: isIndustrializado ? "contain" : "cover"
                    }}
                />
            ) : (
                <RestaurantMenu sx={{ color: "text.disabled" }} />
            )}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 1 }}>
                <Typography fontSize={14} fontWeight={700}>
                    {quantidade}x {descricao}
                </Typography>
                <Typography fontSize={14} fontWeight={700} whiteSpace="nowrap">
                    {real(total)}
                </Typography>
            </Box>
            {subItens.length > 0 ? (
                <Stack spacing={0.5} mt={0.5}>
                    {subItens.map((iv, idx) => (
                        <Stack
                            key={iv.uuid ?? `${iv.descricao}-${idx}`}
                            direction="row"
                            spacing={1}
                            alignItems="flex-start"
                            sx={{ pl: 1, borderLeft: "2px solid", borderColor: "divider" }}
                        >
                            <SubdirectoryArrowRight sx={{ fontSize: 16, color: "text.disabled", mt: "2px" }} />
                            <Box sx={{ flex: 1, display: "flex", justifyContent: "space-between", gap: 1 }}>
                                <Typography fontSize={13}>{iv.quantidade ?? 1}x {iv.descricao}</Typography>
                                {(iv.preco ?? 0) > 0 && (
                                    <Typography fontSize={12} color="text.secondary" whiteSpace="nowrap">
                                        +{real((iv.preco ?? 0) * (iv.quantidade ?? 1))}
                                    </Typography>
                                )}
                            </Box>
                        </Stack>
                    ))}
                </Stack>
            ) : (
                <Stack direction="row" spacing={0.5} alignItems="center" mt={0.25}>
                    <CheckCircle sx={{ fontSize: 14, color: "primary.main" }} />
                    <Typography fontSize={12} color="primary.main">{statusLabel}</Typography>
                </Stack>
            )}
        </Box>
    </Stack>
);

export const DrawerComboOperacao = ({ open, toggleDrawer, produto, adicionarProdutosCombo }: DrawerComboOperacaoProps) => {
    const snackbar = useSnackbar();
    const { real } = useMoeda();

    const comboUuid = useMemo(() => v4(), [open]);

    const itensCombo: IItemCombo[] = produto.itensCombo ?? [];
    const stepsItens = itensCombo.filter(item => item.produto.tipo !== TipoProduto.industrializado);
    const industrializadosItens = itensCombo.filter(item => item.produto.tipo === TipoProduto.industrializado);

    const [step, setStep] = useState(0);

    const [stepsComplementos, setStepsComplementos] = useState<IComplemento[][]>(() =>
        stepsItens.map(item =>
            filtrarComplementosSlot(item.produto, produto.complementos ?? []).map(c => ({ ...c, itensSelecionados: [] }))
        )
    );

    const stepAtual = stepsItens[step];
    const complementosAtual = stepsComplementos[step] ?? [];
    const isLastStep = stepsItens.length === 0 || step === stepsItens.length - 1;

    const definirItensComplemento = (complementoUuid: string, itens: IITemComplemento[]) => {
        setStepsComplementos(prev =>
            prev.map((s, i) =>
                i === step ? s.map(c => c.uuid === complementoUuid ? { ...c, itensSelecionados: itens } : c) : s
            )
        );
    };

    const errosStep = useMemo(() => {
        const erros: string[] = [];
        complementosAtual.forEach(c => {
            if (c.obrigatorio && (!c.itensSelecionados?.length || c.itensSelecionados.length < c.numeroMinimoItens)) {
                erros.push(`O complemento "${c.descricao}" precisa de ${c.numeroMinimoItens} item(ns) selecionado(s)`);
            }
        });
        return erros;
    }, [complementosAtual]);

    const valorTotalCombo = useMemo(() => {
        let total = produto.preco ?? 0;
        industrializadosItens.forEach(item => { total += item.preco * item.quantidade; });
        stepsItens.forEach((item, i) => {
            total += item.preco;
            (stepsComplementos[i] ?? []).forEach(c => {
                total += calcularPrecoComplemento(c);
            });
        });
        return total;
    }, [stepsComplementos]);

    const handleSubmit = () => {
        if (errosStep.length) {
            return snackbar(errosStep[0], { severity: "error" });
        }

        if (!isLastStep) {
            return setStep(s => s + 1);
        }

        const precoBase = produto.preco ?? 0;
        const produtosPedido: IProdutoPedido[] = [];

        stepsItens.forEach((item, i) => {
            const complementos = stepsComplementos[i] ?? [];
            const itens: IItemProduto[] = [];
            const complementosItens: IComplementoItensPedido[] = [];

            complementos.forEach(c => {
                if (c.itensSelecionados?.length) {
                    complementosItens.push({ descricao: c.descricao, uuid: c.uuid, itens: c.itensSelecionados.map(iv => iv.uuid), numeroMinimoItens: c.numeroMinimoItens, numeroMaximoItens: c.numeroMaximoItens });
                    itens.push(...tratarItensComplemento(c));
                }
            });

            produtosPedido.push({
                ...item.produto,
                uuidProdutoReferencia: item.produto.uuid,
                uuidProdutoOrigem: item.produto.produtoOrigemUuid ?? item.produto.uuid,
                descricaoProdutoOrigem: item.produto.descricaoOrigem ?? item.produto.descricao,
                uuid: v4(),
                quantidade: item.quantidade,
                preco: i === 0 ? (item.preco ?? 0) + precoBase : (item.preco ?? 0),
                complementos,
                itens,
                complementosItens,
                tipoProdutoReferencia: item.produto.tipo,
                comboUuid,
                comboReferenciaUuid: produto.uuid,
                comboDescricao: produto.descricao,
                tipo: item.produto.tipo
            });
        });

        industrializadosItens.forEach((item, i) => {
            produtosPedido.push({
                ...item.produto,
                uuidProdutoReferencia: item.produto.uuid,
                uuidProdutoOrigem: item.produto.produtoOrigemUuid ?? item.produto.uuid,
                descricaoProdutoOrigem: item.produto.descricaoOrigem ?? item.produto.descricao,
                uuid: v4(),
                quantidade: item.quantidade,
                preco: stepsItens.length === 0 && i === 0 ? (item.preco ?? 0) + precoBase : (item.preco ?? 0),
                tipoProdutoReferencia: item.produto.tipo,
                comboUuid,
                comboReferenciaUuid: produto.uuid,
                comboDescricao: produto.descricao,
                tipo: item.produto.tipo
            });
        });

        adicionarProdutosCombo(produtosPedido);
        handleClose();
    };

    const handleClose = () => {
        setStep(0);
        setStepsComplementos(
            stepsItens.map(item =>
                filtrarComplementosSlot(item.produto, produto.complementos ?? []).map(c => ({ ...c, itensSelecionados: [] }))
            )
        );
        toggleDrawer();
    };

    return (
        <Drawer anchor="bottom" open={open} onClose={handleClose} sx={{ borderRadius: 0, zIndex: 10002 }}>
            <DetalhesContainer>
                <HeaderDetalhesContainer>
                    {produto.imagens?.length ? (
                        <Avatar alt="Combo" src={produto.imagens[0].url} sx={{ width: 90, height: 90 }} />
                    ) : (
                        <Avatar alt="Combo" src={`${process.env.PUBLIC_URL}/images/produtos/combo.png`} sx={{ width: 90, height: 90 }} />
                    )}
                    <div>
                        <Typography variant="h5" fontWeight="bold">{produto.descricao}</Typography>
                        <Typography variant="h6" fontWeight="bold">{real(valorTotalCombo)}</Typography>
                    </div>
                </HeaderDetalhesContainer>

                {stepsItens.length > 1 && (
                    <Box sx={{ width: "100%", px: 2, mb: 1 }}>
                        <Typography variant="body2" color="GrayText">
                            Item {step + 1} de {stepsItens.length}: {stepAtual?.produto.descricao}
                        </Typography>
                        <LinearProgress variant="determinate" value={((step + 1) / stepsItens.length) * 100} sx={{ mt: 0.5, borderRadius: 1 }} />
                    </Box>
                )}

                <BodyDetalhesContainer>
                    <Grid spacing={1} container height="100%">
                        <Grid item xs={12} sm={6}>
                            <Paper variant="outlined" sx={{ padding: "1rem", height: "100%", overflowY: "auto", gap: "1rem", display: "flex", flexDirection: "column" }}>
                                <Typography variant="h6" fontWeight="bold">
                                    {stepAtual ? stepAtual.produto.descricao : "Itens do combo"}
                                </Typography>
                                {stepAtual ? (
                                    complementosAtual.map(complemento => (
                                        <ComplementoPaperInput
                                            key={complemento.uuid}
                                            complemento={complemento}
                                            definirItensComplemento={definirItensComplemento}
                                        />
                                    ))
                                ) : (
                                    itensCombo.map(item => (
                                        <Typography key={item.produto.uuid} color="GrayText" fontSize={14}>
                                            {item.quantidade}x {item.produto.descricao}
                                        </Typography>
                                    ))
                                )}
                            </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Paper
                                variant="outlined"
                                sx={{ borderRadius: 2, overflow: "hidden", height: "100%", display: "flex", flexDirection: "column" }}
                            >
                                <Box
                                    sx={{
                                        bgcolor: "primary.main",
                                        color: "primary.contrastText",
                                        px: 2,
                                        py: 1.25,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1
                                    }}
                                >
                                    <RestaurantMenu fontSize="small" />
                                    <Typography fontWeight={700} noWrap title={produto.descricao}>
                                        {produto.descricao}
                                    </Typography>
                                </Box>

                                <Stack spacing={2} sx={{ p: 2, flex: 1, overflowY: "auto" }}>
                                    {stepsItens.map((item, i) => {
                                        const subItens = (stepsComplementos[i] ?? []).flatMap(c => tratarItensComplemento(c));
                                        const base = i === 0 ? (produto.preco ?? 0) : 0;
                                        const total =
                                            ((item.preco ?? 0) + base + subItens.reduce((s, x) => s + (x.preco ?? 0) * (x.quantidade ?? 1), 0)) *
                                            (item.quantidade ?? 1);
                                        return (
                                            <ResumoItemCombo
                                                key={item.produto.uuid}
                                                descricao={item.produto.descricao}
                                                quantidade={item.quantidade}
                                                imagem={item.produto.imagens?.length ? item.produto.imagens[0]?.url : undefined}
                                                isIndustrializado={item.produto.tipo === TipoProduto.industrializado}
                                                subItens={subItens}
                                                statusLabel="A configurar"
                                                total={total}
                                                real={real}
                                            />
                                        );
                                    })}
                                    {industrializadosItens.map((item, i) => {
                                        const base = stepsItens.length === 0 && i === 0 ? (produto.preco ?? 0) : 0;
                                        const total = ((item.preco ?? 0) + base) * (item.quantidade ?? 1);
                                        return (
                                            <ResumoItemCombo
                                                key={item.produto.uuid}
                                                descricao={item.produto.descricao}
                                                quantidade={item.quantidade}
                                                imagem={item.produto.imagens?.length ? item.produto.imagens[0]?.url : undefined}
                                                isIndustrializado
                                                subItens={[]}
                                                statusLabel="Incluso no combo"
                                                total={total}
                                                real={real}
                                            />
                                        );
                                    })}
                                </Stack>

                                <Box
                                    sx={{
                                        bgcolor: "action.hover",
                                        px: 2,
                                        py: 1.25,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between"
                                    }}
                                >
                                    <Typography fontSize={12} fontWeight={700} color="text.secondary" letterSpacing="0.05em">
                                        TOTAL DO ITEM
                                    </Typography>
                                    <Typography fontSize={18} fontWeight={700} color="primary.main">
                                        {real(valorTotalCombo)}
                                    </Typography>
                                </Box>

                                <Stack direction="row" spacing={1} sx={{ p: 2, pt: 1.5 }}>
                                    {step > 0 && (
                                        <Button variant="outlined" onClick={() => setStep(s => s - 1)} sx={{ flex: 1, height: "3.5rem" }}>
                                            Voltar
                                        </Button>
                                    )}
                                    <Button variant="contained" onClick={handleSubmit} sx={{ flex: 1, height: "3.5rem" }}>
                                        {isLastStep ? "CONCLUIR" : "PRÓXIMO"}
                                    </Button>
                                </Stack>
                            </Paper>
                        </Grid>
                    </Grid>
                </BodyDetalhesContainer>
            </DetalhesContainer>
        </Drawer>
    );
};
